import {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';

import useFilters from '@/hooks/useFilters';
import {transactionService} from '@/services/transactionService';
import type {
  AfriexAdminTransaction,
  AfriexQueryParams,
  AfriexTransactionStatus,
} from '@/types';
import {formatAdminTransaction} from '@/utils/dashboard';
import {resolveTransactionProcessor} from '@/utils/transactionProcessor';

import type {TransactionListProps} from './types';

const useTransactionList = (): TransactionListProps => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get('status') as AfriexTransactionStatus;
  const [transactionList, setTransactionList] = useState<
    AfriexAdminTransaction[]
  >([]);
  // Starts true because the effect below fetches unconditionally on mount, and
  // useEffect runs after paint. Starting false gave one render with no rows and
  // no loading flag, which flashes the table's "No transactions found" empty
  // state on every page load before the first request has even gone out.
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearch] = useState<boolean>(false);
  const [transactionTotal, setTransactionTotal] = useState<number>(0);
  const [showPagination] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  const [currentFilters, setCurrentFilters] = useState<AfriexQueryParams>({});
  const [exportFilters, setExportFilters] = useState<AfriexQueryParams>({});
  const [filtersToApply, setFiltersToApply] = useState<AfriexQueryParams>({});
  const [filterCount, setFilterCount] = useState(0);
  const {applyFilters} = useFilters(setCurrentFilters);

  // The `status` URL param is layered onto the filters the request is built
  // from rather than mirrored into `currentFilters` state by an effect. That
  // effect ran after the first render and always handed `currentFilters` a
  // fresh object identity, which recreated the fetch callback and fired a
  // second, identical request on every mount. `currentFilters` now holds only
  // the filters the admin picked, so its identity is stable and this memo is
  // stable with it.
  const effectiveFilters = useMemo(
    () => (status ? {...currentFilters, status} : currentFilters),
    [currentFilters, status],
  );

  const fetchAllTransactions = useCallback(
    async (params?: AfriexQueryParams) => {
      try {
        setError(null);
        setIsLoading(true);
        const query = {...params, ...effectiveFilters};
        const transactionData =
          await transactionService.getTransactionList(query);
        const {page: _page, limit: _limit, ...filters} = query;
        setExportFilters(filters);

        const filtered = await Promise.all(
          transactionData?.data &&
            transactionData?.data.map(async (transaction) => {
              const resolved = await resolveTransactionProcessor(transaction);
              const at = await formatAdminTransaction(resolved);
              return {
                ...resolved,
                type: at.type,
                adminUser: at.adminUser,
              };
            }),
        );
        setTransactionList(filtered);
        setTransactionTotal(transactionData?.total || 0);
      } catch (error) {
        // Drop the previous page's rows so they do not render under the error.
        setTransactionList([]);
        setError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [effectiveFilters],
  );

  useEffect(() => {
    // Fire-and-forget: fetchAllTransactions catches its own failures into
    // `error`, so this only stops an unexpected throw becoming an unhandled
    // rejection.
    fetchAllTransactions({}).catch(() => {});
  }, [fetchAllTransactions]);

  const handleApplyFilters = useCallback(() => {
    // Layer the URL `status` onto a copy instead of mutating the state object.
    // `applyFilters` receives exactly the same filters as before; the
    // difference is that `filtersToApply` state is no longer written behind
    // React's back, so it stays owned by `selectAppliedFilters`.
    const applied = applyFilters(
      status ? {...filtersToApply, status} : filtersToApply,
    );

    // Pagination reset is success-only work. Flipping it unconditionally made a
    // rejected date range fire a request anyway: Pagination watches this value
    // and calls setPage(1), which calls onPageChange -> a refetch. The user got
    // an error toast, an unnecessary request, and a jump back to page one.
    if (applied) {
      setIsPageReset((prev) => !prev);
    }

    return applied;
  }, [applyFilters, filtersToApply, status]);

  // Handle change for user manipulation
  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  // Handle dates for applied filters
  const selectAppliedFilters = useCallback(
    (name: string, value: string | number | Date | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Count only filters that are actually set. Counting `!== undefined`
        // kept an emptied text field (which holds '') in the tally, so Apply
        // stayed enabled and the label read `Apply Filters (1)` with nothing
        // filled in. This is the same emptiness rule `useFilters.applyFilters`
        // already applies one layer down, so the count and the applied set now
        // agree. No filter value is a meaningful 0.
        const filteredCount =
          Object.values(updatedFilters).filter(Boolean).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );

  const cleanFilters = () => {
    setCurrentFilters({});
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
    if (status) {
      setCurrentFilters({status});
    }
  };

  return {
    error,
    fetchTransactionList: fetchAllTransactions,
    isLoading,
    isSearch,
    setPage,
    pageInfo: {
      page,
      total: transactionTotal,
    },
    showPagination,
    transactionList,
    exportFilters,
    filterCount,
    isPageReset,
    currentFilters,
    filtersToApply,
    clearFilters: cleanFilters,
    selectAppliedFilters,
    handleApplyFilters,
    selectCurrentFilters,
  };
};

export default useTransactionList;
